import React, { KeyboardEvent, ReactNode, useEffect } from "react";
import Flex from "common/Boxes/Flex";
import styled, { css, useTheme } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens";
import SubmitButton from "features/onboarding/common/SubmitButton";
import Text from "common/Typography/Text";
import {
  CODE_BOX_HEIGHT,
  onboardingStyles,
} from "@shared/features/onboarding/styles";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import useVerificationCode from "@shared/features/onboarding/useVerificationCode";
import { openMessage } from "utils/misc";
import Link from "common/Typography/Link.tsx";
import { IS_WEB_VIEW } from "common/constants.ts";
import { requestClipboardData } from "services/webview/webViewBridgeUtils.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import {
  AUTH_VERIFICATION_CODE_INPUT_ID,
  AUTH_VERIFICATION_CODE_SUBMIT_BUTTON_ID,
} from "testIds.ts";

interface VerificationCodeProps {
  numOfDigits: number;
  title?: string;
  onSubmit: (code: string) => void;
  isLoading: boolean;
  buttonText?: string;
  defaultCode?: string;
  actionButton?: ReactNode;
  additionalItems?: ReactNode;
}

const VerificationCode = ({
  numOfDigits,
  title,
  onSubmit,
  isLoading,
  buttonText,
  defaultCode,
  actionButton,
  additionalItems,
}: VerificationCodeProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const { isNewLook } = useTheme();
  const {
    onBackPress,
    hiddenInputProps,
    buttonProps,
    boxesData,
    processClipboardCode,
  } = useVerificationCode({
    numOfDigits,
    onSubmit,
    openMessage,
    defaultCode,
    autoFocus: !IS_WEB_VIEW && !IS_TELEGRAM_WEB_APP,
  });

  const isClipboardAvailable =
    IS_TELEGRAM_WEB_APP &&
    !!navigator.clipboard &&
    !!navigator.clipboard.readText;

  const pasteClipboardCode = async (isAutoPaste?: boolean) => {
    if (IS_WEB_VIEW) {
      try {
        const clipboardData = await requestClipboardData();
        await processClipboardCode(clipboardData.clipboardText, isAutoPaste);
      } catch {}
    } else if (isClipboardAvailable) {
      try {
        const clipboardText = await navigator.clipboard.readText();
        await processClipboardCode(clipboardText, isAutoPaste);
      } catch {}
    }
  };

  const checkClipboardForCode = () => {
    try {
      pasteClipboardCode(true).catch(() => {});
    } catch {}
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkClipboardForCode();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace") {
      onBackPress();
    }
  };

  const verificationCode = (
    <div style={{ position: "relative" }}>
      <Flex $justifyContent="center">
        {boxesData.map((boxData, index) => {
          return (
            <InputBox
              key={index}
              style={boxData.style}
              $isSelected={boxData.isSelected}
              onClick={boxData.onClick}
            >
              <Text $size={"L"}>{boxData.text}</Text>
            </InputBox>
          );
        })}
      </Flex>
      <HiddenInput
        value={hiddenInputProps.value}
        ref={hiddenInputProps.ref}
        onChange={hiddenInputProps.onChange}
        onKeyDown={onKeyPress}
        onBlur={hiddenInputProps.onBlur}
        onFocus={hiddenInputProps.onFocus}
        stringMode={true}
        // Lets iOS and Android offer the code from Messages or Mail.
        autoComplete="one-time-code"
        data-testid={AUTH_VERIFICATION_CODE_INPUT_ID}
      />
    </div>
  );

  return (
    <CodeWrapper>
      <InputTitle>{title}</InputTitle>
      {verificationCode}
      {(actionButton || isClipboardAvailable) && (
        <Flex style={{ marginTop: 8 }}>
          {actionButton ?? <div />}
          {(isClipboardAvailable || IS_WEB_VIEW) && (
            <Link onClick={() => pasteClipboardCode()}>
              {t(
                isNewLook
                  ? "verification_code.paste_code_new_look"
                  : "verification_code.paste_code"
              )}
            </Link>
          )}
        </Flex>
      )}
      {additionalItems}
      <StyledSubmitButton
        onClick={buttonProps.onClick}
        disabled={buttonProps.disabled}
        loading={isLoading}
        data-testid={AUTH_VERIFICATION_CODE_SUBMIT_BUTTON_ID}
      >
        {buttonText || t("verification_code.verify")}
      </StyledSubmitButton>
    </CodeWrapper>
  );
};

export default VerificationCode;

const CodeWrapper = styled.div`
  width: 100%;
  max-width: 340px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      /* The code spans the panel, as every other field does. */
      max-width: none;
    `}
`;
const InputBox = styled.div<{ $isSelected: boolean }>`
  ${onboardingStyles.verificationCodeBox};
  border-style: solid;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      height: 48px;
      border-radius: ${p.theme.radii.control}px;
      border-width: 1px;
      border-color: ${p.$isSelected
        ? p.theme.colors.primaryBlue
        : p.theme.colors.inputBorderColor};
      background: ${p.theme.colors.cardBgColor};
      box-shadow: ${p.$isSelected
        ? `0 0 0 2px ${getNewLookTokens(p.theme).focus}`
        : "none"};
      transition: border-color 150ms ease, box-shadow 150ms ease;
      cursor: text;
      span {
        ${newLookType.figureLarge};
        color: ${p.theme.colors.titleColor};
      }
    `}
`;
const InputTitle = styled(Text)`
  margin: 32px 0 8px 0;
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      display: block;
      margin: 0 0 8px;
      ${newLookType.label};
      color: ${p.theme.colors.secondaryText};
      text-align: start;
    `}
`;
const StyledSubmitButton = styled(SubmitButton)`
  width: 100%;
  margin: 24px 0 12px 0;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      margin: 24px 0 0;
    `}
`;
const HiddenInput = styled(CustomInputNumber)`
  position: absolute;
  top: 0;
  left: 0;
  height: ${CODE_BOX_HEIGHT}px;
  opacity: 0;
`;
